import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Trash,
  ArrowSquareOut,
  Plus,
  PencilSimple,
  Check,
  ArrowUp,
  ArrowDown,
  Users,
  Package,
  ClockCounterClockwise,
  Eye,
  EyeSlash,
  Bell,
  DeviceMobile,
  BellRinging,
  MagnifyingGlass,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_EMAIL = "swarajrp030@gmail.com";

const TAB_ANALYTICS = "analytics";
const TAB_CATEGORIES = "categories";
const TAB_BRANDS = "brands";
const TAB_CAMPAIGNS = "campaigns";
const TAB_PENDING = "pending";
const TAB_USERS = "users";

const AdminPanel = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const initialTab = [TAB_ANALYTICS, TAB_CATEGORIES, TAB_BRANDS, TAB_CAMPAIGNS, TAB_PENDING, TAB_USERS].includes(tabParam) ? tabParam : TAB_ANALYTICS;
  const [tab, setTab] = useState(initialTab);
  const [user, setUser] = useState(null);
  const [authChecking, setAuthChecking] = useState(true);

  useEffect(() => {
    const check = async () => {
      try {
        const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (me.data?.email !== ADMIN_EMAIL) {
          toast.error("Admins only");
          navigate("/");
          return;
        }
        setUser(me.data);
      } catch {
        toast.error("Please sign in");
        navigate("/login");
      } finally {
        setAuthChecking(false);
      }
    };
    check();
  }, [navigate]);

  const switchTab = (t) => {
    setTab(t);
    setSearchParams({ tab: t }, { replace: true });
  };

  if (authChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]" data-testid="admin-loading">
        <p className="text-[#6B6A68]">Checking access…</p>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="admin-panel-page">
      <header className="sticky top-0 z-40 bg-[#F9F8F6]/90 backdrop-blur-xl border-b border-[#E4E1D9] py-4 px-6 md:px-12 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/")}
            className="p-2 hover:bg-[#F0EFEB] rounded-md transition-colors"
            data-testid="admin-back-button"
            aria-label="Back to discovery"
          >
            <ArrowLeft size={20} weight="bold" className="text-[#2D302A]" />
          </button>
          <h1
            className="text-2xl md:text-3xl tracking-tighter"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            Admin Panel
          </h1>
        </div>
      </header>

      {/* Tabs */}
      <div className="sticky top-[65px] md:top-[73px] z-30 bg-[#F9F8F6] border-b border-[#E4E1D9] px-6 md:px-12">
        <nav className="flex gap-6 md:gap-10 overflow-x-auto" role="tablist">
          <TabButton active={tab === TAB_ANALYTICS} onClick={() => switchTab(TAB_ANALYTICS)} testid="tab-analytics">
            Analytics
          </TabButton>
          <TabButton active={tab === TAB_CATEGORIES} onClick={() => switchTab(TAB_CATEGORIES)} testid="tab-categories">
            Categories
          </TabButton>
          <TabButton active={tab === TAB_BRANDS} onClick={() => switchTab(TAB_BRANDS)} testid="tab-brands">
            Brands
          </TabButton>
          <TabButton active={tab === TAB_CAMPAIGNS} onClick={() => switchTab(TAB_CAMPAIGNS)} testid="tab-campaigns">
            Campaigns
          </TabButton>
          <TabButton active={tab === TAB_PENDING} onClick={() => switchTab(TAB_PENDING)} testid="tab-pending">
            Pending Submissions
          </TabButton>
          <TabButton active={tab === TAB_USERS} onClick={() => switchTab(TAB_USERS)} testid="tab-users">
            Users
          </TabButton>
        </nav>
      </div>

      <main className="max-w-4xl mx-auto px-6 md:px-12 py-10">
        {tab === TAB_ANALYTICS && <AnalyticsDashboard />}
        {tab === TAB_CATEGORIES && <CategoryManager />}
        {tab === TAB_BRANDS && <BrandManager />}
        {tab === TAB_CAMPAIGNS && <CampaignManager />}
        {tab === TAB_PENDING && <PendingSubmissions />}
        {tab === TAB_USERS && <UserSearch />}
      </main>
    </div>
  );
};

const TabButton = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    role="tab"
    aria-selected={active}
    data-testid={testid}
    className={`py-4 text-xs uppercase tracking-[0.2em] border-b-2 transition-colors ${
      active
        ? "border-[#2D302A] text-[#2D302A]"
        : "border-transparent text-[#6B6A68] hover:text-[#2D302A]"
    }`}
    style={{ fontFamily: "IBM Plex Mono, monospace" }}
  >
    {children}
  </button>
);

// ----------------------- User Search -----------------------

const formatValue = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

const UserSearch = () => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(null);

  const search = async (e) => {
    e.preventDefault();
    const q = query.trim();
    if (q.length < 2) {
      toast.error("Type at least 2 characters");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users/search`, {
        params: { q },
        withCredentials: true,
      });
      setResults(res.data.items || []);
      setOpenId(res.data.items?.length === 1 ? res.data.items[0].user_id : null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-user-search">
      <h2
        className="text-3xl tracking-tighter mb-6"
        style={{ fontFamily: "Cormorant Garamond, serif" }}
      >
        Find a user
      </h2>
      <form onSubmit={search} className="flex gap-2 mb-8">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Name, username or email"
          className="flex-1 min-w-0 border border-[#E4E1D9] bg-white rounded-md px-4 py-3 text-sm outline-none focus:border-[#2D302A]"
          data-testid="admin-user-search-input"
        />
        <button
          type="submit"
          disabled={loading}
          className="inline-flex items-center gap-2 bg-[#2D302A] text-white px-5 rounded-md text-xs uppercase tracking-[0.2em] hover:bg-[#1A1A1A] disabled:opacity-60"
          style={{ fontFamily: "IBM Plex Mono, monospace" }}
          data-testid="admin-user-search-button"
        >
          <MagnifyingGlass size={16} weight="bold" />
          {loading ? "…" : "Search"}
        </button>
      </form>

      {results && results.length === 0 && (
        <p className="text-[#6B6A68] text-sm">No users match that search.</p>
      )}

      {results && results.length > 0 && (
        <div className="space-y-3">
          <p
            className="text-[11px] uppercase tracking-[0.2em] text-[#6B6A68]"
            style={{ fontFamily: "IBM Plex Mono, monospace" }}
          >
            {results.length} result{results.length === 1 ? "" : "s"}
            {results.length === 50 ? " (showing first 50)" : ""}
          </p>
          {results.map((u) => {
            const open = openId === u.user_id;
            return (
              <div
                key={u.user_id}
                className="bg-white border border-[#E4E1D9] rounded-md"
                data-testid="admin-user-result"
              >
                <button
                  type="button"
                  onClick={() => setOpenId(open ? null : u.user_id)}
                  className="w-full flex items-center gap-3 p-4 text-left hover:bg-[#FAF9F5]"
                  aria-expanded={open}
                >
                  {u.picture ? (
                    <img src={u.picture} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-[#E9E3D6] flex items-center justify-center text-sm text-[#2D302A] shrink-0">
                      {(u.name || u.email || "?").charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-[#2D302A] truncate">
                      {u.name || "—"}
                      {u.username && <span className="text-[#9A9A98]"> · @{u.username}</span>}
                    </p>
                    <p className="text-xs text-[#6B6A68] truncate">{u.email}</p>
                  </div>
                  <span className="text-[#9A9A98] text-xs">{open ? "Hide" : "Details"}</span>
                </button>
                {open && (
                  <dl className="border-t border-[#F0EFEB] px-4 py-3 grid grid-cols-[minmax(0,10rem)_1fr] gap-x-4 gap-y-2 text-sm">
                    {Object.keys(u)
                      .sort()
                      .map((k) => (
                        <div key={k} className="contents">
                          <dt
                            className="text-[11px] uppercase tracking-[0.1em] text-[#6B6A68] pt-0.5 break-words"
                            style={{ fontFamily: "IBM Plex Mono, monospace" }}
                          >
                            {k.replace(/_/g, " ")}
                          </dt>
                          <dd className="text-[#2D302A] break-all select-all">{formatValue(u[k])}</dd>
                        </div>
                      ))}
                  </dl>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ----------------------- Analytics Dashboard -----------------------

const GENDER_OPTIONS = [
  { value: "", label: "All" },
  { value: "men", label: "Men" },
  { value: "women", label: "Women" },
];

const AnalyticsDashboard = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [gender, setGender] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // App install & push adoption. Not date/gender filterable, so fetched once
  // and shown in its own group below the filtered platform stats.
  const [pwa, setPwa] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (gender) params.gender = gender;
      const res = await axios.get(`${API}/admin/analytics`, {
        params,
        withCredentials: true,
      });
      setData(res.data);
    } catch (err) {
      setError(true);
      toast.error(err?.response?.data?.detail || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  // Reload whenever a filter changes.
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, gender]);

  // App/push adoption is unfiltered — fetch once on mount.
  useEffect(() => {
    axios
      .get(`${API}/admin/pwa-metrics`, { withCredentials: true })
      .then((res) => setPwa(res.data))
      .catch(() => setPwa(null));
  }, []);

  const clearFilters = () => {
    setStartDate("");
    setEndDate("");
    setGender("");
  };

  const hasFilters = startDate || endDate || gender;

  const cards = [
    {
      key: "registrations",
      label: "Registrations",
      hint: "Users who signed up",
      icon: Users,
      value: data?.registrations,
    },
    {
      key: "products",
      label: "Products Posted",
      hint: "Live fits on the platform",
      icon: Package,
      value: data?.products,
    },
    {
      key: "pending",
      label: "Pending Submissions",
      hint: "Links awaiting review",
      icon: ClockCounterClockwise,
      value: data?.pending,
    },
  ];

  const pwaCards = [
    {
      key: "pwa_installs",
      label: "App Installs",
      hint: "Recorded install events (lower bound)",
      icon: DeviceMobile,
      value: pwa?.pwa_installs,
    },
    {
      key: "push_users",
      label: "Push-On Users",
      hint: "Users with notifications enabled",
      icon: Bell,
      value: pwa?.push_users,
    },
    {
      key: "push_subscriptions",
      label: "Push Subscriptions",
      hint: "Live devices/browsers subscribed",
      icon: BellRinging,
      value: pwa?.push_subscriptions,
    },
  ];

  const labelCls =
    "block text-[10px] uppercase tracking-[0.2em] mb-1.5 text-[#6B6A68]";
  const inputCls =
    "px-3 py-2 bg-white border border-[#E4E1D9] rounded-md focus:outline-none focus:ring-1 focus:ring-[#2D302A] text-sm";

  return (
    <section data-testid="analytics-dashboard">
      <p className="text-sm text-[#6B6A68] mb-6">
        A snapshot of platform activity. Use the filters to narrow by date range and gender.
      </p>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 mb-8 p-4 bg-white border border-[#E4E1D9] rounded-md">
        <div>
          <label className={labelCls} style={{ fontFamily: "IBM Plex Mono, monospace" }}>
            From
          </label>
          <input
            type="date"
            value={startDate}
            max={endDate || undefined}
            onChange={(e) => setStartDate(e.target.value)}
            className={inputCls}
            data-testid="analytics-start-date"
          />
        </div>
        <div>
          <label className={labelCls} style={{ fontFamily: "IBM Plex Mono, monospace" }}>
            To
          </label>
          <input
            type="date"
            value={endDate}
            min={startDate || undefined}
            onChange={(e) => setEndDate(e.target.value)}
            className={inputCls}
            data-testid="analytics-end-date"
          />
        </div>
        <div>
          <label className={labelCls} style={{ fontFamily: "IBM Plex Mono, monospace" }}>
            Gender
          </label>
          <select
            value={gender}
            onChange={(e) => setGender(e.target.value)}
            className={inputCls}
            data-testid="analytics-gender"
          >
            {GENDER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        {hasFilters && (
          <button
            onClick={clearFilters}
            className="px-4 py-2 text-xs uppercase tracking-[0.2em] text-[#6B6A68] hover:text-[#2D302A] border border-[#E4E1D9] rounded-md hover:border-[#2D302A] transition-colors"
            style={{ fontFamily: "IBM Plex Mono, monospace" }}
            data-testid="analytics-clear-filters"
          >
            Clear
          </button>
        )}
      </div>

      {/* Stat cards */}
      {error ? (
        <div className="border border-dashed border-[#E4E1D9] rounded-md p-10 text-center">
          <p className="text-[#6B6A68]">Couldn’t load analytics. Try again.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="analytics-cards">
          {cards.map((c) => {
            const Icon = c.icon;
            return (
              <div
                key={c.key}
                className="bg-white border border-[#E4E1D9] rounded-md p-5"
                data-testid={`analytics-card-${c.key}`}
              >
                <div className="flex items-center gap-2 text-[#6B6A68] mb-3">
                  <Icon size={18} weight="bold" />
                  <span
                    className="text-[10px] uppercase tracking-[0.2em]"
                    style={{ fontFamily: "IBM Plex Mono, monospace" }}
                  >
                    {c.label}
                  </span>
                </div>
                <div
                  className="text-4xl md:text-5xl tracking-tighter text-[#2D302A]"
                  style={{ fontFamily: "Cormorant Garamond, serif" }}
                  data-testid={`analytics-value-${c.key}`}
                >
                  {loading || c.value == null ? "-" : c.value.toLocaleString()}
                </div>
                <div className="text-xs text-[#6B6A68] mt-1">{c.hint}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* App install & push adoption (unfiltered) */}
      <div className="mt-10" data-testid="pwa-metrics">
        <p
          className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68] mb-3"
          style={{ fontFamily: "IBM Plex Mono, monospace" }}
        >
          App & Notifications
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="pwa-cards">
          {pwaCards.map((c) => {
            const Icon = c.icon;
            return (
              <div
                key={c.key}
                className="bg-white border border-[#E4E1D9] rounded-md p-5"
                data-testid={`pwa-card-${c.key}`}
              >
                <div className="flex items-center gap-2 text-[#6B6A68] mb-3">
                  <Icon size={18} weight="bold" />
                  <span
                    className="text-[10px] uppercase tracking-[0.2em]"
                    style={{ fontFamily: "IBM Plex Mono, monospace" }}
                  >
                    {c.label}
                  </span>
                </div>
                <div
                  className="text-4xl md:text-5xl tracking-tighter text-[#2D302A]"
                  style={{ fontFamily: "Cormorant Garamond, serif" }}
                  data-testid={`pwa-value-${c.key}`}
                >
                  {c.value == null ? "-" : c.value.toLocaleString()}
                </div>
                <div className="text-xs text-[#6B6A68] mt-1">{c.hint}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ----------------------- Brand Manager -----------------------
const BrandManager = () => {
  const [brands, setBrands] = useState([]);
  const [name, setName] = useState("");
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/brands`, { withCredentials: true });
      setBrands(res.data || []);
    } catch {
      toast.error("Failed to load brands");
    }
  };
  useEffect(() => {
    load();
  }, []);

  const handleLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setLogo(f);
    setLogoPreview(URL.createObjectURL(f));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Enter a brand name");
    if (!logo) return toast.error("Upload a logo");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("name", name.trim());
      fd.append("logo", logo);
      await axios.post(`${API}/brands`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });
      toast.success("Brand added");
      setName("");
      setLogo(null);
      setLogoPreview(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add brand");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, bname) => {
    if (!window.confirm(`Delete "${bname}"? It'll be removed from any products using it.`)) return;
    try {
      await axios.delete(`${API}/brands/${id}`, { withCredentials: true });
      toast.success("Brand deleted");
      load();
    } catch {
      toast.error("Failed to delete brand");
    }
  };

  return (
    <section data-testid="brand-manager">
      <p className="text-sm text-[#6B6A68] mb-6">
        Brands can be tagged on a product (shown as a logo on the product image). Add a brand with its logo below.
      </p>

      <form
        onSubmit={handleCreate}
        className="flex flex-wrap items-end gap-3 mb-8 p-4 bg-white border border-[#E4E1D9] rounded-md"
      >
        <div className="flex-1 min-w-[160px]">
          <label className="block text-xs uppercase tracking-[0.2em] mb-1 text-[#6B6A68]" style={{ fontFamily: "IBM Plex Mono, monospace" }}>
            Brand name
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Myntra"
            className="w-full px-3 py-2 bg-white border border-[#E4E1D9] rounded-md focus:outline-none focus:ring-1 focus:ring-[#2D302A]"
            data-testid="brand-name-input"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.2em] mb-1 text-[#6B6A68]" style={{ fontFamily: "IBM Plex Mono, monospace" }}>
            Logo
          </label>
          <input type="file" accept="image/*" onChange={handleLogo} className="text-xs" data-testid="brand-logo-input" />
        </div>
        {logoPreview && (
          <img src={logoPreview} alt="" className="w-10 h-10 rounded-full object-cover border border-[#E4E1D9]" />
        )}
        <button
          type="submit"
          disabled={saving}
          className="bg-[#2D302A] text-white px-5 py-2 rounded-md text-xs uppercase tracking-[0.15em] hover:bg-[#1A1C18] disabled:opacity-50"
          style={{ fontFamily: "IBM Plex Mono, monospace" }}
          data-testid="brand-add-button"
        >
          {saving ? "Adding…" : "Add brand"}
        </button>
      </form>

      {brands.length === 0 ? (
        <p className="text-sm text-[#6B6A68]">No brands yet.</p>
      ) : (
        <div className="space-y-2">
          {brands.map((b) => (
            <div
              key={b.id}
              className="flex items-center gap-3 p-3 bg-white border border-[#E4E1D9] rounded-md"
              data-testid={`brand-row-${b.id}`}
            >
              {b.logo_url && (
                <img src={`${BACKEND_URL}${b.logo_url}`} alt={b.name} className="w-10 h-10 rounded-full object-cover border border-[#E4E1D9]" />
              )}
              <span className="flex-1 text-sm">{b.name}</span>
              <button
                onClick={() => handleDelete(b.id, b.name)}
                className="p-2 text-red-600 hover:bg-[#F0EFEB] rounded-md transition-colors"
                aria-label="Delete brand"
                data-testid={`brand-delete-${b.id}`}
              >
                <Trash size={18} weight="bold" />
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

// ----------------------- Campaign Manager -----------------------
// Marketing banners: upload a finished creative and target it by gender,
// category tab, audience, and timing. Rendered in the feed as an inline row
// or a timed popup; clicking a banner opens the Share-a-Fit form.
const GENDER_LABELS = { all: "Everyone", male: "Men", female: "Women" };
const AUDIENCE_LABELS = { all: "Everyone", registered: "Registered users", guest: "Guests" };
const FREQ_LABELS = {
  once_per_day: "Once per day",
  once_per_session: "Once per session",
  every_load: "Every page load",
};

const EMPTY_CAMPAIGN = {
  title: "",
  placement: "inline",
  gender: "all",
  category_tabs: ["all"],
  audience: "all",
  delay_seconds: 5,
  inline_after: 6,
  frequency: "once_per_day",
};

const CampaignManager = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(EMPTY_CAMPAIGN);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = creating a new one
  const [editImageUrl, setEditImageUrl] = useState(null); // existing banner while editing
  const formRef = useRef(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/campaigns`, { withCredentials: true });
      setCampaigns(res.data || []);
    } catch {
      toast.error("Failed to load campaigns");
    }
  };
  useEffect(() => {
    load();
    axios
      .get(`${API}/categories`, { withCredentials: true })
      .then((res) => setCategories(res.data || []))
      .catch(() => setCategories([]));
  }, []);

  const setField = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImage(f);
    setImagePreview(URL.createObjectURL(f));
  };

  // Category-tab multi-select. "All" is exclusive; picking a specific tab
  // clears "All", and clearing everything falls back to "All".
  const toggleTab = (id) => {
    setForm((p) => {
      if (id === "all") return { ...p, category_tabs: ["all"] };
      const current = p.category_tabs.filter((t) => t !== "all");
      const next = current.includes(id)
        ? current.filter((t) => t !== id)
        : [...current, id];
      return { ...p, category_tabs: next.length ? next : ["all"] };
    });
  };

  const resetForm = () => {
    setForm(EMPTY_CAMPAIGN);
    setImage(null);
    setImagePreview(null);
    setEditingId(null);
    setEditImageUrl(null);
  };

  // Load an existing campaign into the form for editing, and scroll it in view.
  const startEdit = (c) => {
    setEditingId(c.id);
    setForm({
      title: c.title || "",
      placement: c.placement || "inline",
      gender: c.gender || "all",
      category_tabs: c.category_tabs?.length ? c.category_tabs : ["all"],
      audience: c.audience || "all",
      delay_seconds: c.delay_seconds ?? 5,
      inline_after: c.inline_after ?? 6,
      frequency: c.frequency || "once_per_day",
    });
    setImage(null);
    setImagePreview(null);
    setEditImageUrl(c.image_url ? `${BACKEND_URL}${c.image_url}` : null);
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Creating requires a banner; editing keeps the existing one unless replaced.
    if (!editingId && !image) return toast.error("Upload a banner image");
    setSaving(true);
    try {
      if (editingId) {
        // Update settings (JSON), then swap the image only if a new one was picked.
        await axios.put(
          `${API}/admin/campaigns/${editingId}`,
          {
            title: form.title.trim(),
            placement: form.placement,
            gender: form.gender,
            category_tabs: form.category_tabs,
            audience: form.audience,
            delay_seconds: Number(form.delay_seconds) || 0,
            inline_after: Number(form.inline_after) || 0,
            frequency: form.frequency,
          },
          { withCredentials: true }
        );
        if (image) {
          const fd = new FormData();
          fd.append("image", image);
          await axios.put(`${API}/admin/campaigns/${editingId}/image`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
            withCredentials: true,
          });
        }
        toast.success("Campaign updated");
      } else {
        const fd = new FormData();
        fd.append("image", image);
        fd.append("title", form.title.trim());
        fd.append("placement", form.placement);
        fd.append("gender", form.gender);
        fd.append("category_tabs", form.category_tabs.join(","));
        fd.append("audience", form.audience);
        fd.append("delay_seconds", String(form.delay_seconds || 0));
        fd.append("inline_after", String(form.inline_after || 0));
        fd.append("frequency", form.frequency);
        fd.append("active", "true");
        await axios.post(`${API}/admin/campaigns`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true,
        });
        toast.success("Campaign created");
      }
      resetForm();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save campaign");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (c) => {
    try {
      await axios.put(
        `${API}/admin/campaigns/${c.id}`,
        { active: !c.active },
        { withCredentials: true }
      );
      load();
    } catch {
      toast.error("Failed to update campaign");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this campaign? This can't be undone.")) return;
    try {
      await axios.delete(`${API}/admin/campaigns/${id}`, { withCredentials: true });
      toast.success("Campaign deleted");
      load();
    } catch {
      toast.error("Failed to delete campaign");
    }
  };

  const catName = (id) => categories.find((c) => c.id === id)?.name || id;
  const tabsSummary = (tabs) =>
    !tabs || tabs.includes("all") ? "All tabs" : tabs.map(catName).join(", ");

  const isInline = form.placement === "inline";
  const labelCls = "block text-xs uppercase tracking-[0.2em] mb-1 text-[#6B6A68]";
  const selCls = "w-full px-3 py-2 bg-white border border-[#E4E1D9] rounded-md focus:outline-none focus:ring-1 focus:ring-[#2D302A] text-sm";
  const mono = { fontFamily: "IBM Plex Mono, monospace" };

  return (
    <section data-testid="campaign-manager">
      <p className="text-sm text-[#6B6A68] mb-6">
        Upload a marketing banner and choose who sees it, where, and when. Banners open the Share-a-Fit form when clicked.
      </p>

      <form
        ref={formRef}
        onSubmit={handleSubmit}
        className={`space-y-5 mb-10 p-5 bg-white border rounded-md ${editingId ? "border-[#2D302A] ring-1 ring-[#2D302A]" : "border-[#E4E1D9]"}`}
        data-testid="campaign-form"
      >
        {editingId && (
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-[0.2em] text-[#2D302A]" style={mono}>
              Editing campaign
            </span>
            <button
              type="button"
              onClick={resetForm}
              className="text-xs text-[#6B6A68] hover:text-[#2D302A] hover:underline"
              style={mono}
              data-testid="campaign-cancel-edit"
            >
              Cancel
            </button>
          </div>
        )}

        {/* Banner image */}
        <div>
          <label className={labelCls} style={mono}>
            {editingId ? "Banner image (leave empty to keep current)" : "Banner image"}
          </label>
          <input type="file" accept="image/*" onChange={handleImage} className="text-xs" data-testid="campaign-image-input" />
          {(imagePreview || editImageUrl) && (
            <img
              src={imagePreview || editImageUrl}
              alt=""
              className="mt-3 w-full max-w-xl rounded-md border border-[#E4E1D9]"
            />
          )}
        </div>

        {/* Internal title */}
        <div>
          <label className={labelCls} style={mono}>Title (internal label)</label>
          <input
            value={form.title}
            onChange={setField("title")}
            placeholder="e.g. Diwali share push"
            className={selCls}
            data-testid="campaign-title-input"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Placement */}
          <div>
            <label className={labelCls} style={mono}>Show as</label>
            <select value={form.placement} onChange={setField("placement")} className={selCls} style={mono} data-testid="campaign-placement">
              <option value="inline">Inline row (in the grid)</option>
              <option value="popup">Popup</option>
            </select>
          </div>

          {/* Placement-specific timing */}
          {isInline ? (
            <div>
              <label className={labelCls} style={mono}>After how many products</label>
              <input
                type="number"
                min="0"
                value={form.inline_after}
                onChange={setField("inline_after")}
                className={selCls}
                data-testid="campaign-inline-after"
              />
            </div>
          ) : (
            <div>
              <label className={labelCls} style={mono}>Delay (seconds on page)</label>
              <input
                type="number"
                min="0"
                value={form.delay_seconds}
                onChange={setField("delay_seconds")}
                className={selCls}
                data-testid="campaign-delay"
              />
            </div>
          )}

          {/* Who — gender */}
          <div>
            <label className={labelCls} style={mono}>Who (gender)</label>
            <select value={form.gender} onChange={setField("gender")} className={selCls} style={mono} data-testid="campaign-gender">
              {Object.entries(GENDER_LABELS).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>

          {/* Audience */}
          <div>
            <label className={labelCls} style={mono}>Audience</label>
            <select value={form.audience} onChange={setField("audience")} className={selCls} style={mono} data-testid="campaign-audience">
              {Object.entries(AUDIENCE_LABELS).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>

          {/* How often — applies to both inline rows and popups */}
          <div>
            <label className={labelCls} style={mono}>How often</label>
            <select value={form.frequency} onChange={setField("frequency")} className={selCls} style={mono} data-testid="campaign-frequency">
              {Object.entries(FREQ_LABELS).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Where — category tabs */}
        <div>
          <label className={labelCls} style={mono}>Where (category tabs)</label>
          <div className="flex flex-wrap gap-2" data-testid="campaign-tabs">
            {[{ id: "all", name: "All tabs" }, ...categories].map((c) => {
              const active = form.category_tabs.includes(c.id);
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => toggleTab(c.id)}
                  className={`px-3 py-1.5 rounded-full border text-xs transition-colors ${
                    active
                      ? "bg-[#2D302A] text-white border-[#2D302A]"
                      : "bg-white text-[#2D302A] border-[#E4E1D9] hover:border-[#2D302A]"
                  }`}
                  style={mono}
                  data-testid={`campaign-tab-${c.id}`}
                >
                  {c.name}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={saving}
            className="bg-[#2D302A] text-white px-5 py-2.5 rounded-md text-xs uppercase tracking-[0.15em] hover:bg-[#1A1C18] disabled:opacity-50"
            style={mono}
            data-testid="campaign-add-button"
          >
            {saving ? "Saving…" : editingId ? "Save changes" : "Create campaign"}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={resetForm}
              className="px-5 py-2.5 rounded-md text-xs uppercase tracking-[0.15em] border border-[#E4E1D9] text-[#2D302A] hover:bg-[#F0EFEB]"
              style={mono}
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      {campaigns.length === 0 ? (
        <p className="text-sm text-[#6B6A68]">No campaigns yet.</p>
      ) : (
        <div className="space-y-3" data-testid="campaign-list">
          {campaigns.map((c) => (
            <div
              key={c.id}
              className={`flex gap-4 p-3 bg-white border rounded-md ${c.active ? "border-[#E4E1D9]" : "border-dashed border-[#C9C5BA] opacity-70"}`}
              data-testid={`campaign-row-${c.id}`}
            >
              <img
                src={`${BACKEND_URL}${c.image_url}`}
                alt={c.title || "banner"}
                className="w-28 h-16 object-cover rounded border border-[#E4E1D9] shrink-0"
              />
              <div className="flex-1 min-w-0 text-xs text-[#6B6A68] space-y-1" style={mono}>
                <div className="text-sm text-[#2D302A] truncate">{c.title || "(untitled)"}</div>
                <div>
                  {c.placement === "inline"
                    ? `Inline · after ${c.inline_after} · ${FREQ_LABELS[c.frequency] || c.frequency}`
                    : `Popup · ${c.delay_seconds}s · ${FREQ_LABELS[c.frequency] || c.frequency}`}
                </div>
                <div>
                  {GENDER_LABELS[c.gender]} · {AUDIENCE_LABELS[c.audience]} · {tabsSummary(c.category_tabs)}
                </div>
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <button
                  onClick={() => startEdit(c)}
                  className="p-2 text-[#2D302A] hover:bg-[#F0EFEB] rounded-md transition-colors"
                  aria-label="Edit campaign"
                  title="Edit campaign"
                  data-testid={`campaign-edit-${c.id}`}
                >
                  <PencilSimple size={18} weight="bold" />
                </button>
                <button
                  onClick={() => toggleActive(c)}
                  className="p-2 text-[#2D302A] hover:bg-[#F0EFEB] rounded-md transition-colors"
                  aria-label={c.active ? "Pause campaign" : "Activate campaign"}
                  title={c.active ? "Active — click to pause" : "Paused — click to activate"}
                  data-testid={`campaign-toggle-${c.id}`}
                >
                  {c.active ? <Eye size={18} weight="bold" /> : <EyeSlash size={18} weight="bold" />}
                </button>
                <button
                  onClick={() => handleDelete(c.id)}
                  className="p-2 text-red-600 hover:bg-[#F0EFEB] rounded-md transition-colors"
                  aria-label="Delete campaign"
                  data-testid={`campaign-delete-${c.id}`}
                >
                  <Trash size={18} weight="bold" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

// ----------------------- Category Manager -----------------------

const CategoryManager = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [newKind, setNewKind] = useState("clothing");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editHeights, setEditHeights] = useState("");
  const [editKind, setEditKind] = useState("clothing");
  const [editSizeLabel, setEditSizeLabel] = useState("ft");
  const [editTypes, setEditTypes] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/categories`, { withCredentials: true });
      setCategories(res.data || []);
    } catch {
      toast.error("Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      // Shoe tabs default to UK sizes + shoe types so they're usable immediately;
      // everything stays editable below.
      const isShoes = newKind === "shoes";
      const body = {
        name,
        kind: newKind,
        size_label: isShoes ? "UK" : "ft",
        types: isShoes ? ["Sports", "Casual", "Loafer"] : ["Upper", "Lower"],
        heights: isShoes ? ["6", "7", "8", "9", "10", "11"] : undefined,
      };
      await axios.post(`${API}/categories`, body, { withCredentials: true });
      setNewName("");
      setNewKind("clothing");
      toast.success(`Created "${name}"`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create category");
    } finally {
      setCreating(false);
    }
  };

  const handleStartEdit = (cat) => {
    setEditingId(cat.id);
    setEditName(cat.name);
    setEditHeights((cat.heights || []).join(", "));
    setEditKind(cat.kind === "shoes" ? "shoes" : "clothing");
    setEditSizeLabel(cat.size_label || "ft");
    setEditTypes((cat.types || []).join(", "));
  };

  const parseList = (raw) =>
    raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

  const handleSaveEdit = async (id) => {
    const name = editName.trim();
    if (!name) {
      toast.error("Name cannot be empty");
      return;
    }
    const heights = parseList(editHeights);
    if (heights.length === 0) {
      toast.error("Add at least one size");
      return;
    }
    const types = parseList(editTypes);
    if (types.length === 0) {
      toast.error("Add at least one type");
      return;
    }
    setBusyId(id);
    try {
      await axios.put(
        `${API}/categories/${id}`,
        {
          name,
          heights,
          kind: editKind,
          size_label: editSizeLabel.trim() || "ft",
          types,
        },
        { withCredentials: true }
      );
      toast.success("Category saved");
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? Products in this category will become uncategorized.`)) return;
    setBusyId(id);
    try {
      await axios.delete(`${API}/categories/${id}`, { withCredentials: true });
      toast.success("Category deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete");
    } finally {
      setBusyId(null);
    }
  };

  const handleMove = async (id, direction) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/categories/${id}/move`, { direction }, { withCredentials: true });
      load();
    } catch (err) {
      toast.error("Failed to reorder");
    } finally {
      setBusyId(null);
    }
  };

  // Toggle whether this tab's products appear in the aggregated "All" feed.
  const handleToggleShowInAll = async (cat) => {
    const next = !(cat.show_in_all !== false);
    setBusyId(cat.id);
    try {
      await axios.put(
        `${API}/categories/${cat.id}`,
        { show_in_all: next },
        { withCredentials: true }
      );
      toast.success(next ? `"${cat.name}" shows in All` : `"${cat.name}" hidden from All`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section data-testid="category-manager">
      <p className="text-sm text-[#6B6A68] mb-6">
        Categories show up as tabs on the homepage. <span className="text-[#2D302A]">All</span> is always shown as the default tab. Pick <span className="text-[#2D302A]">Shoes</span> for a shoe tab — it uses UK sizes and shoe types (Sports/Casual/Loafer) instead of ft and Upper/Lower. Edit any tab to fine-tune its sizes, unit, and types. Use the <span className="text-[#2D302A]">In All</span> toggle to control whether a tab's products appear in the default All feed.
      </p>

      <form
        onSubmit={handleCreate}
        className="flex gap-3 items-stretch mb-8"
        data-testid="category-create-form"
      >
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="New tab name (e.g., Men, Women, Men Shoes)"
          className="flex-1 px-4 py-3 bg-white border border-[#E4E1D9] rounded-md focus:outline-none focus:ring-1 focus:ring-[#2D302A]"
          maxLength={50}
          data-testid="category-name-input"
        />
        <select
          value={newKind}
          onChange={(e) => setNewKind(e.target.value)}
          className="px-3 py-3 bg-white border border-[#E4E1D9] rounded-md focus:outline-none focus:ring-1 focus:ring-[#2D302A] text-sm"
          style={{ fontFamily: "IBM Plex Mono, monospace" }}
          data-testid="category-kind-select"
          title="Clothing tabs use ft + Upper/Lower; Shoes tabs use UK sizes + shoe types"
        >
          <option value="clothing">Clothing</option>
          <option value="shoes">Shoes</option>
        </select>
        <button
          type="submit"
          disabled={creating || !newName.trim()}
          className="px-5 bg-[#2D302A] text-white rounded-md hover:bg-[#1A1C18] transition-colors disabled:opacity-50 flex items-center gap-2"
          style={{ fontFamily: "IBM Plex Mono, monospace" }}
          data-testid="category-create-button"
        >
          <Plus size={18} weight="bold" />
          <span className="text-xs uppercase tracking-[0.2em]">Add</span>
        </button>
      </form>

      {loading ? (
        <p className="text-center py-10 text-[#6B6A68]">Loading…</p>
      ) : categories.length === 0 ? (
        <div
          className="border border-dashed border-[#E4E1D9] rounded-md p-10 text-center"
          data-testid="category-empty"
        >
          <p className="text-[#6B6A68]">
            No categories yet. Create one above to show it as a tab on the homepage.
          </p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="category-list">
          {categories.map((cat, idx) => {
            const isEditing = editingId === cat.id;
            const isBusy = busyId === cat.id;
            return (
              <li
                key={cat.id}
                className="bg-white border border-[#E4E1D9] rounded-md px-4 py-3"
                data-testid={`category-item-${idx}`}
              >
                <div className="flex items-center gap-3">
                  <div className="flex flex-col gap-0.5">
                    <button
                      onClick={() => handleMove(cat.id, "up")}
                      disabled={idx === 0 || isBusy}
                      className="p-1 text-[#6B6A68] hover:text-[#2D302A] disabled:opacity-30 disabled:cursor-not-allowed"
                      aria-label="Move up"
                      data-testid={`category-move-up-${idx}`}
                    >
                      <ArrowUp size={14} weight="bold" />
                    </button>
                    <button
                      onClick={() => handleMove(cat.id, "down")}
                      disabled={idx === categories.length - 1 || isBusy}
                      className="p-1 text-[#6B6A68] hover:text-[#2D302A] disabled:opacity-30 disabled:cursor-not-allowed"
                      aria-label="Move down"
                      data-testid={`category-move-down-${idx}`}
                    >
                      <ArrowDown size={14} weight="bold" />
                    </button>
                  </div>

                  <div className="flex-1 min-w-0">
                    {isEditing ? (
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Escape") setEditingId(null);
                        }}
                        autoFocus
                        maxLength={50}
                        className="w-full px-3 py-2 border border-[#E4E1D9] rounded focus:outline-none focus:ring-1 focus:ring-[#2D302A]"
                        data-testid={`category-edit-input-${idx}`}
                      />
                    ) : (
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span
                            className="text-base md:text-lg tracking-tight truncate"
                            style={{ fontFamily: "Cormorant Garamond, serif" }}
                            data-testid={`category-name-${idx}`}
                          >
                            {cat.name}
                          </span>
                          <span
                            className={`shrink-0 text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded ${
                              cat.kind === "shoes"
                                ? "bg-[#B23A48]/10 text-[#B23A48]"
                                : "bg-[#F0EFEB] text-[#6B6A68]"
                            }`}
                            style={{ fontFamily: "IBM Plex Mono, monospace" }}
                            data-testid={`category-kind-${idx}`}
                          >
                            {cat.kind === "shoes" ? "Shoes" : "Clothing"}
                          </span>
                        </div>
                        <div
                          className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68] truncate"
                          style={{ fontFamily: "IBM Plex Mono, monospace" }}
                          data-testid={`category-heights-${idx}`}
                        >
                          Sizes ({cat.size_label || "ft"}): {(cat.heights || []).join(", ") || "-"} · Types: {(cat.types || []).join(", ") || "-"}
                        </div>
                      </div>
                    )}
                  </div>

                  {isEditing ? (
                    <>
                      <button
                        onClick={() => handleSaveEdit(cat.id)}
                        disabled={isBusy}
                        className="px-3 py-1.5 text-xs uppercase tracking-[0.2em] bg-[#2D302A] text-white rounded hover:bg-[#1A1C18] transition-colors flex items-center gap-1"
                        style={{ fontFamily: "IBM Plex Mono, monospace" }}
                        data-testid={`category-save-${idx}`}
                      >
                        <Check size={14} weight="bold" /> Save
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        className="px-3 py-1.5 text-xs uppercase tracking-[0.2em] text-[#6B6A68] hover:text-[#2D302A]"
                        style={{ fontFamily: "IBM Plex Mono, monospace" }}
                        data-testid={`category-cancel-${idx}`}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => handleToggleShowInAll(cat)}
                        disabled={isBusy}
                        className={`flex items-center gap-1 px-2 py-1.5 rounded transition-colors text-[10px] uppercase tracking-[0.15em] ${
                          cat.show_in_all !== false
                            ? "text-[#2D302A] hover:bg-[#F0EFEB]"
                            : "text-[#B23A48] hover:bg-[#F0EFEB]"
                        }`}
                        style={{ fontFamily: "IBM Plex Mono, monospace" }}
                        aria-label={
                          cat.show_in_all !== false
                            ? `Hide ${cat.name} from All feed`
                            : `Show ${cat.name} in All feed`
                        }
                        title="Whether this tab's products appear in the All feed"
                        data-testid={`category-toggle-all-${idx}`}
                      >
                        {cat.show_in_all !== false ? (
                          <Eye size={15} weight="bold" />
                        ) : (
                          <EyeSlash size={15} weight="bold" />
                        )}
                        <span className="hidden sm:inline">
                          {cat.show_in_all !== false ? "In All" : "Hidden"}
                        </span>
                      </button>
                      <button
                        onClick={() => handleStartEdit(cat)}
                        disabled={isBusy}
                        className="p-2 text-[#6B6A68] hover:text-[#2D302A] hover:bg-[#F0EFEB] rounded transition-colors"
                        aria-label={`Rename ${cat.name}`}
                        data-testid={`category-edit-${idx}`}
                      >
                        <PencilSimple size={16} weight="bold" />
                      </button>
                      <button
                        onClick={() => handleDelete(cat.id, cat.name)}
                        disabled={isBusy}
                        className="p-2 text-[#6B6A68] hover:text-red-600 hover:bg-[#F0EFEB] rounded transition-colors"
                        aria-label={`Delete ${cat.name}`}
                      data-testid={`category-delete-${idx}`}
                    >
                      <Trash size={16} weight="bold" />
                    </button>
                  </>
                )}
                </div>

                {isEditing && (
                  <div className="mt-3 pt-3 border-t border-[#F0EFEB] space-y-3" data-testid={`category-heights-editor-${idx}`}>
                    {/* Kind + size unit */}
                    <div className="flex flex-wrap gap-3">
                      <div>
                        <label
                          className="block text-[10px] uppercase tracking-[0.2em] mb-1.5 text-[#6B6A68]"
                          style={{ fontFamily: "IBM Plex Mono, monospace" }}
                        >
                          Kind
                        </label>
                        <select
                          value={editKind}
                          onChange={(e) => {
                            const k = e.target.value;
                            setEditKind(k);
                            // Nudge the unit label to a sensible default on switch.
                            setEditSizeLabel((prev) =>
                              k === "shoes" && (!prev || prev === "ft") ? "UK"
                              : k === "clothing" && (!prev || prev === "UK") ? "ft"
                              : prev
                            );
                          }}
                          className="px-3 py-2 border border-[#E4E1D9] rounded focus:outline-none focus:ring-1 focus:ring-[#2D302A] text-sm"
                          style={{ fontFamily: "IBM Plex Mono, monospace" }}
                          data-testid={`category-edit-kind-${idx}`}
                        >
                          <option value="clothing">Clothing</option>
                          <option value="shoes">Shoes</option>
                        </select>
                      </div>
                      <div>
                        <label
                          className="block text-[10px] uppercase tracking-[0.2em] mb-1.5 text-[#6B6A68]"
                          style={{ fontFamily: "IBM Plex Mono, monospace" }}
                        >
                          Size unit label
                        </label>
                        <input
                          type="text"
                          value={editSizeLabel}
                          onChange={(e) => setEditSizeLabel(e.target.value)}
                          placeholder="ft / UK / EU / cm"
                          maxLength={8}
                          className="w-28 px-3 py-2 border border-[#E4E1D9] rounded focus:outline-none focus:ring-1 focus:ring-[#2D302A]"
                          style={{ fontFamily: "IBM Plex Mono, monospace" }}
                          data-testid={`category-edit-size-label-${idx}`}
                        />
                      </div>
                    </div>

                    <div>
                      <label
                        className="block text-[10px] uppercase tracking-[0.2em] mb-1.5 text-[#6B6A68]"
                        style={{ fontFamily: "IBM Plex Mono, monospace" }}
                      >
                        Sizes for this tab (comma-separated)
                      </label>
                      <input
                        type="text"
                        value={editHeights}
                        onChange={(e) => setEditHeights(e.target.value)}
                        placeholder={editKind === "shoes" ? "e.g., 6, 7, 8, 9, 10, 11" : "e.g., 6.0, 6.1, 6.2, 6.3, 6.4, 6.5"}
                        className="w-full px-3 py-2 border border-[#E4E1D9] rounded focus:outline-none focus:ring-1 focus:ring-[#2D302A]"
                        style={{ fontFamily: "IBM Plex Mono, monospace" }}
                        data-testid={`category-heights-input-${idx}`}
                      />
                      <p className="text-[10px] text-[#6B6A68] mt-1">
                        The size options shown in the homepage filter and the product form for this tab.
                      </p>
                    </div>

                    <div>
                      <label
                        className="block text-[10px] uppercase tracking-[0.2em] mb-1.5 text-[#6B6A68]"
                        style={{ fontFamily: "IBM Plex Mono, monospace" }}
                      >
                        Types for this tab (comma-separated)
                      </label>
                      <input
                        type="text"
                        value={editTypes}
                        onChange={(e) => setEditTypes(e.target.value)}
                        placeholder={editKind === "shoes" ? "e.g., Sports, Casual, Loafer" : "e.g., Upper, Lower"}
                        className="w-full px-3 py-2 border border-[#E4E1D9] rounded focus:outline-none focus:ring-1 focus:ring-[#2D302A]"
                        style={{ fontFamily: "IBM Plex Mono, monospace" }}
                        data-testid={`category-types-input-${idx}`}
                      />
                      <p className="text-[10px] text-[#6B6A68] mt-1">
                        Replaces Upper/Lower — the sub-type buttons in the product form and the type filter on the homepage.
                      </p>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

// ----------------------- Pending Submissions (moved in) -----------------------

const PendingSubmissions = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [filter, setFilter] = useState("all"); // all | not_added | added

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/pending-submissions`, { withCredentials: true });
        setSubmissions(res.data || []);
      } catch {
        toast.error("Failed to load pending submissions");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Records are never deleted from here — the admin just flips whether the
  // submission has been turned into a live product.
  const handleToggleAdded = async (s) => {
    setSavingId(s.id);
    try {
      const res = await axios.patch(
        `${API}/pending-submissions/${s.id}`,
        { added: !s.added },
        { withCredentials: true }
      );
      setSubmissions((prev) =>
        prev.map((x) => (x.id === s.id ? { ...x, added: res.data.added, added_at: res.data.added_at } : x))
      );
    } catch {
      toast.error("Couldn't update status");
    } finally {
      setSavingId(null);
    }
  };

  const handleCopyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy link");
    }
  };

  if (loading) return <p className="text-center py-10 text-[#6B6A68]" data-testid="pending-loading-list">Loading…</p>;

  if (submissions.length === 0) {
    return (
      <div className="text-center py-16" data-testid="pending-empty">
        <h2
          className="text-3xl md:text-4xl mb-3 tracking-tighter"
          style={{ fontFamily: "Cormorant Garamond, serif" }}
        >
          All clear
        </h2>
        <p className="text-[#6B6A68]">No pending submissions right now.</p>
      </div>
    );
  }

  const addedCount = submissions.filter((s) => s.added).length;
  const counts = {
    all: submissions.length,
    not_added: submissions.length - addedCount,
    added: addedCount,
  };
  const visible = submissions.filter((s) =>
    filter === "added" ? s.added : filter === "not_added" ? !s.added : true
  );

  return (
    <section data-testid="pending-section">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-[#6B6A68]">
          Review each link, then create the live product yourself using <span className="text-[#2D302A]">Share a Fit</span> when worth featuring.
        </p>
        <span
          className="text-xs uppercase tracking-[0.2em] text-[#6B6A68]"
          style={{ fontFamily: "IBM Plex Mono, monospace" }}
          data-testid="pending-count"
        >
          {submissions.length} {submissions.length === 1 ? "item" : "items"}
        </span>
      </div>
      <div className="flex flex-wrap gap-2 mb-6" role="tablist" data-testid="pending-filter">
        {[
          ["all", "All"],
          ["not_added", "Not added"],
          ["added", "Added"],
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setFilter(key)}
            aria-pressed={filter === key}
            className={`px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.15em] border transition-colors ${
              filter === key
                ? "bg-[#2D302A] text-white border-[#2D302A]"
                : "bg-white text-[#6B6A68] border-[#E4E1D9] hover:border-[#2D302A]"
            }`}
            style={{ fontFamily: "IBM Plex Mono, monospace" }}
            data-testid={`pending-filter-${key}`}
          >
            {label} · {counts[key]}
          </button>
        ))}
      </div>
      {visible.length === 0 && (
        <p className="text-sm text-[#6B6A68] py-6">Nothing here.</p>
      )}
      <div className="space-y-6" data-testid="pending-list">
        {visible.map((s, idx) => (
          <article
            key={s.id}
            className="bg-white border border-[#E4E1D9] rounded-md p-4 md:p-5 flex flex-col md:flex-row gap-5"
            data-testid={`pending-item-${idx}`}
          >
            {s.image_url ? (
              <img
                src={`${BACKEND_URL}${s.image_url}`}
                alt={s.product_name || "Pending submission"}
                className="w-full md:w-40 h-40 object-cover rounded border border-[#E4E1D9]"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            ) : (
              <div className="w-full md:w-40 h-40 bg-[#F0EFEB] border border-[#E4E1D9] rounded flex items-center justify-center text-xs text-[#6B6A68]">
                No image
              </div>
            )}

            <div className="flex-1 min-w-0">
              <h3
                className="text-xl md:text-2xl tracking-tight mb-2 line-clamp-2"
                style={{ fontFamily: "Cormorant Garamond, serif" }}
              >
                {s.product_name || "Untitled"}
              </h3>
              <a
                href={s.product_link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-[#2D302A] hover:underline break-all"
              >
                {s.product_link.length > 80 ? s.product_link.slice(0, 80) + "…" : s.product_link}
                <ArrowSquareOut size={14} weight="bold" />
              </a>
              <div className="text-xs text-[#6B6A68] mt-2">
                Submitted by {s.user_name || "Unknown"}
                {s.user_email ? ` · ${s.user_email}` : ""} · {new Date(s.created_at).toLocaleString()}
              </div>
              {(s.fit_feedback || s.height || s.waist_size || s.shoe_size) && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {s.fit_feedback && (
                    <span
                      data-testid={`pending-fit-${idx}`}
                      className="inline-block px-2 py-1 rounded text-[10px] uppercase tracking-[0.2em] bg-[#F0EFEB] text-[#2D302A] border border-[#E4E1D9]"
                      style={{ fontFamily: "IBM Plex Mono, monospace" }}
                    >
                      Fit:{" "}
                      {s.fit_feedback === "short"
                        ? "A little short"
                        : s.fit_feedback === "long"
                        ? "A little long"
                        : "Perfect"}
                    </span>
                  )}
                  {s.height && (
                    <span
                      data-testid={`pending-height-${idx}`}
                      className="inline-block px-2 py-1 rounded text-[10px] uppercase tracking-[0.2em] bg-[#F0EFEB] text-[#2D302A] border border-[#E4E1D9]"
                      style={{ fontFamily: "IBM Plex Mono, monospace" }}
                    >
                      Height: {s.height}
                    </span>
                  )}
                  {s.waist_size && (
                    <span
                      data-testid={`pending-waist-${idx}`}
                      className="inline-block px-2 py-1 rounded text-[10px] uppercase tracking-[0.2em] bg-[#F0EFEB] text-[#2D302A] border border-[#E4E1D9]"
                      style={{ fontFamily: "IBM Plex Mono, monospace" }}
                    >
                      Waist: {s.waist_size}"
                    </span>
                  )}
                  {s.shoe_size && (
                    <span
                      data-testid={`pending-shoe-${idx}`}
                      className="inline-block px-2 py-1 rounded text-[10px] uppercase tracking-[0.2em] bg-[#F0EFEB] text-[#2D302A] border border-[#E4E1D9]"
                      style={{ fontFamily: "IBM Plex Mono, monospace" }}
                    >
                      Shoe: UK {s.shoe_size}
                    </span>
                  )}
                </div>
              )}
              <div className="flex flex-wrap gap-2 mt-4">
                <button
                  onClick={() => handleCopyLink(s.product_link)}
                  className="px-3 py-2 text-xs uppercase tracking-[0.2em] bg-white border border-[#E4E1D9] hover:border-[#2D302A] rounded transition-colors"
                  style={{ fontFamily: "IBM Plex Mono, monospace" }}
                  data-testid={`pending-copy-${idx}`}
                >
                  Copy Link
                </button>
                <button
                  type="button"
                  role="switch"
                  aria-checked={!!s.added}
                  onClick={() => handleToggleAdded(s)}
                  disabled={savingId === s.id}
                  className={`px-3 py-2 text-xs uppercase tracking-[0.2em] border rounded transition-colors flex items-center gap-2 disabled:opacity-50 ${
                    s.added
                      ? "bg-[#E7F0E4] border-[#9DBB95] text-[#2F5A27]"
                      : "bg-white border-[#E4E1D9] text-[#6B6A68] hover:border-[#2D302A]"
                  }`}
                  style={{ fontFamily: "IBM Plex Mono, monospace" }}
                  title={s.added_at ? `Marked added ${new Date(s.added_at).toLocaleString()}` : "Mark as added"}
                  data-testid={`pending-added-toggle-${idx}`}
                >
                  <span
                    className={`relative inline-block w-7 h-4 rounded-full transition-colors ${
                      s.added ? "bg-[#4E8A43]" : "bg-[#C9C5BA]"
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${
                        s.added ? "left-3.5" : "left-0.5"
                      }`}
                    />
                  </span>
                  {s.added ? "Added" : "Not added"}
                </button>
                {s.added_notified_at ? (
                  <span
                    className="self-center text-[11px] text-[#6B6A68]"
                    title={new Date(s.added_notified_at).toLocaleString()}
                    data-testid={`pending-notified-${idx}`}
                  >
                    Contributor notified
                  </span>
                ) : s.added ? (
                  <span className="self-center text-[11px] text-[#9A9A98]">
                    Notifies contributor after 5 min
                  </span>
                ) : null}
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

export default AdminPanel;
